import {FilterMatchMode, PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {Column, type ColumnFilterElementTemplateOptions} from "primereact/column";
import {DataTable, type DataTableFilterMeta} from "primereact/datatable";
import {MultiSelect} from "primereact/multiselect";
import {Toast} from "primereact/toast";
import {Tooltip} from "primereact/tooltip";
import {type MouseEvent, type RefObject, useRef, useState} from "react";

import {MimeType} from "@reva/shared/mime-type";
import type {ValueOrderDocument} from "@reva/shared/value-order";

import {filterApplyTemplate, filterClearTemplate} from "../../component/dataTableFilterTemplates";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {selectDocuments} from "../../redux/documentsSlice";
import {DownloadToastMessage, downloadDocumentAsync} from "../../redux/documentsThunk";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import {openBlobForDownload} from "../../util/browserUtil";
import {formatDateAndTime} from "../../util/dateUtil";

/**
 * Widths of the columns whose content has a bounded length, so that the filename column — the only one
 * left to grow — gets all of the remaining room: the names of the generated reports are long and wrap
 * badly. Each value is what its widest expected content needs at the table's own font size, plus a
 * buffer: 50 % on top of "Wertgutachten aller Art" for the document type, about 10 % elsewhere. The
 * date has to fit the English format ("Oct 13, 2026, 2:11 PM"), which is wider than the German one, and
 * the IDs are measured as monospace UUIDs, whose glyph width differs a little between platforms.
 */
const COLUMN_WIDTH = {
    icon: "2em",
    documentType: "18em",
    creationDate: "12em",
    id: "23em",
} as const;

/**
 * The two ID columns are secondary information — only of interest to technical users — and together they
 * take 46 em. Below the width where the filename column can still show the generated report names on two
 * lines (measured at 2149 px, rounded up) they are hidden, so the filenames get that room instead. A
 * one-off arbitrary variant rather than a sixth global screen: no other place in REVA needs this bound.
 */
const ID_COLUMN_VISIBILITY = "hidden min-[2200px]:table-cell";

/**
 * Pin a column to exactly one width. In the table's automatic layout `width` alone is only a hint the
 * browser trades off against the content, so the surplus space only reaches the filename column when the
 * bounds are given as well.
 */
function fixedWidth(width: string) {
    return {width, minWidth: width, maxWidth: width};
}

/** Component displaying a filetype icon. */
function DocumentIcon({rowData}: {rowData: ValueOrderDocument}) {
    let icon: string;
    switch (rowData.extension) {
        case "xlsx":
        case "xls":
            icon = PrimeIcons.FILE_EXCEL;
            break;
        case "pdf":
            icon = PrimeIcons.FILE_PDF;
            break;
        case "png":
        case "jpg":
        case "jpeg":
            icon = PrimeIcons.IMAGE;
            break;
        default:
            icon = PrimeIcons.FILE;
    }
    // Tooltip target class is matched by the <Tooltip> in DocumentsTable.
    return <i className={`${icon} document-type-icon`} data-pr-tooltip={rowData.extension} />;
}

/** Component displaying a download link. */
function DownloadButton({document, toast}: {document: ValueOrderDocument; toast: RefObject<Toast | null>}) {
    const dispatch = useRevaDispatch();

    const mimeTypeForExtension = (ext: string) => {
        switch (ext.toLowerCase()) {
            case "jpg":
            case "jpeg":
                return MimeType.JPEG;
            case "png":
                return MimeType.PNG;
            case "xlsx":
                return MimeType.XLSX;
            case "xls":
                return MimeType.XLS;
            default:
                return MimeType.PDF;
        }
    };

    const onDownload = async (event: MouseEvent) => {
        event.preventDefault();
        try {
            const downloadedBlob = await dispatch(downloadDocumentAsync(document));
            openBlobForDownload(downloadedBlob, document.filename, mimeTypeForExtension(document.extension));
        } catch (e) {
            if (toast.current && e instanceof DownloadToastMessage) {
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: e.errorMessage,
                    life: 3000,
                });
            } else {
                console.error(e);
            }
        }
    };

    return <Button label={document.filename} icon={PrimeIcons.DOWNLOAD} onClick={(e) => void onDownload(e)} text />;
}

export default function DocumentsTable() {
    const {t, language} = useRevaTranslation();

    const toast = useRef<Toast>(null);

    // Column filter on document type, e.g. show only "Bild" (#18860).
    const [filters, setFilters] = useState<DataTableFilterMeta>({
        documentType: {value: null, matchMode: FilterMatchMode.IN},
    });

    const documents = useRevaSelector(selectDocuments);
    if (!documents) {
        return null;
    }

    // Filter overlay styled like the valuations table on the start page: filter icon in
    // the column header, multiselect over the occurring types, apply/clear buttons.
    const documentTypes = [...new Set(documents.map((d) => d.documentType).filter(Boolean))].sort();
    const typeFilterElement = (options: ColumnFilterElementTemplateOptions) => (
        <>
            <div className="mb-2">{t("documents.filterType")}</div>
            <MultiSelect
                value={options.value}
                options={documentTypes}
                onChange={(e) => options.filterCallback(e.value)}
                maxSelectedLabels={1}
                showClear
                style={{minWidth: "16rem"}}
            />
        </>
    );
    return (
        <>
            <Toast ref={toast} />
            <Tooltip target=".document-type-icon" position="top" />
            <DataTable<ValueOrderDocument[]>
                value={documents}
                emptyMessage={t("documents.emptyMessage")}
                removableSort
                size="small"
                filters={filters}
                onFilter={(e) => setFilters(e.filters)}
            >
                <Column
                    field="extension"
                    style={fixedWidth(COLUMN_WIDTH.icon)}
                    body={(rowData: ValueOrderDocument) => <DocumentIcon rowData={rowData} />}
                />
                <Column
                    field="documentType"
                    style={fixedWidth(COLUMN_WIDTH.documentType)}
                    header={t("documents.col.type")}
                    sortable
                    filter
                    showFilterMenuOptions={false}
                    filterElement={typeFilterElement}
                    filterApply={filterApplyTemplate}
                    filterClear={filterClearTemplate}
                />
                <Column
                    field="filename"
                    style={{
                        minWidth: "16em",
                    }}
                    header={t("documents.col.filename")}
                    body={(document: ValueOrderDocument) => <DownloadButton document={document} toast={toast} />}
                    sortable
                />
                <Column
                    field="creationDate"
                    style={fixedWidth(COLUMN_WIDTH.creationDate)}
                    bodyClassName="whitespace-nowrap"
                    header={t("documents.col.date")}
                    // The raw field stays the sort key: it is an ISO date-time, so sorting it as a string
                    // is chronological, while the formatted text would sort by day of month.
                    body={(document: ValueOrderDocument) => formatDateAndTime(document.creationDate, language)}
                    sortable
                />
                <Column
                    field="documentId"
                    style={fixedWidth(COLUMN_WIDTH.id)}
                    headerClassName={ID_COLUMN_VISIBILITY}
                    bodyClassName={`font-mono whitespace-nowrap ${ID_COLUMN_VISIBILITY}`}
                    header={t("documents.col.id")}
                />
                <Column
                    field="valueOrderGuid"
                    style={fixedWidth(COLUMN_WIDTH.id)}
                    headerClassName={ID_COLUMN_VISIBILITY}
                    bodyClassName={`font-mono whitespace-nowrap ${ID_COLUMN_VISIBILITY}`}
                    header={t("documents.col.orderId")}
                />
            </DataTable>
        </>
    );
}
